import { useQuery } from '@apollo/client/react';
import { DimeButton, DimeCardShellSkeleton, DimeDetailPageStates } from '@nudool/design-system';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Building2Icon } from 'lucide-react';
import { useBreadcrumbLabel } from '../../components/BreadcrumbLabelsContext.js';
import { assertUuidRouteParams } from '../../lib/assert-uuid-route-params.js';
import {
  parseCustomerDetailSearch,
  type CustomerDetailSearchPatch,
} from '../../modules/customers/customer-ar-ledger-search.js';
import { CustomerDetailView } from '../../modules/customers/customer-detail.js';
import { customerQueryVariables } from '../../modules/customers/customer-query-variables.js';
import {
  GET_CUSTOMER,
  type GetCustomerQueryResult,
  type GetCustomerQueryVariables,
} from '../../modules/customers/operations.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';

export const Route = createFileRoute('/customers/$customerId')({
  beforeLoad: ({ params }) => assertUuidRouteParams(params),
  validateSearch: (search: Record<string, unknown>) => parseCustomerDetailSearch(search),
  component: CustomerDetailPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function CustomerDetailPage() {
  const navigate = useNavigate();
  const { customerId } = Route.useParams();
  const search = Route.useSearch();
  const { permissions } = usePermissions();
  const { data, loading, error, refetch } = useQuery<
    GetCustomerQueryResult,
    GetCustomerQueryVariables
  >(GET_CUSTOMER, {
    variables: customerQueryVariables(customerId, permissions),
  });
  useBreadcrumbLabel(customerId, data?.customer?.name);

  // The tab and Account Activity filters live in the same URL, so every write keeps what the rest of it holds.
  const updateSearch = (patch: CustomerDetailSearchPatch) => {
    void navigate({
      to: '/customers/$customerId',
      params: { customerId },
      search: { ...search, ...patch },
      replace: true,
    });
  };

  return (
    <DimeDetailPageStates
      data={data?.customer}
      loading={loading}
      error={error}
      entityLabel="Customer"
      notFoundIcon={<Building2Icon />}
      notFoundAction={
        <DimeButton variant="outline" asChild>
          <Link to="/customers">Back to customers</Link>
        </DimeButton>
      }
      skeleton={<DimeCardShellSkeleton factCount={2} actionCount={4} tabCount={4} rowCount={5} />}
      onRetry={() => void refetch().catch(() => undefined)}
    >
      {(customer) => (
        <CustomerDetailView
          customer={customer}
          onChanged={() => void refetch()}
          search={search}
          onSearchChange={updateSearch}
          onNewOrder={(brandId) =>
            void navigate({ to: '/sales-orders/new', search: { customerId, brandId } })
          }
        />
      )}
    </DimeDetailPageStates>
  );
}
